// src/core/assets/images.ts
// All static image/SVG URL assets served from public/images/
// Every value is a string (URL path) — no React components here.

// ── Logo ─────────────────────────────────────────────────────────────────────
import logo from "/images/logo/logo.svg";

// ── Page-header icons ────────────────────────────────────────────────────────
import WorkspaceIcon from "/images/page-header/workspace-icon.svg";
import DocsIcon from "/images/page-header/docs-icon.svg";
import ApprovalIcon from "/images/page-header/approval-icon.svg";
import MembersIcon from "/images/page-header/members-icon.svg";
import TrashIcon from "/images/page-header/trash-icon.svg";
import ProjectsIcon from "/images/page-header/projects-icon.svg";
import LogsIcon from "/images/page-header/logs-icon.svg";
import SettingsIcon from "/images/page-header/setting-icon.svg";
import LicenseIcon from "/images/page-header/license-icon.svg";
import PortalIcon from "/images/page-header/portal-header.svg";
import ManageAccessIcon from "/images/page-header/manage-access-icon.svg";

// ── Empty states ─────────────────────────────────────────────────────────────
import NoAdmins from "/images/empty-state/no_admins.svg";
import NoDocs from "/images/empty-state/no_docs.svg";
import EmptyWorkspace from "/images/empty-state/no_workspaces.svg";
import NoWorkspaceProjects from "/images/empty-state/no-workspace-projects.svg";
import NoWorkspaceProjectsTable from "/images/empty-state/no_projects_table.svg";
import NoMembers from "/images/empty-state/no_members.svg";
import NoActiveApprovals from "/images/empty-state/no_active_approvals.svg";
import NoRecentActivity from "/images/empty-state/no_recent_activity.svg";
import NoSearchDocs from "/images/empty-state/no_search-docs.svg";
import NoRecentDocuments from "/images/empty-state/no_recent_doc.svg";
import NoLicense from "/images/empty-state/no-license.svg";
import NoInivtes from "/images/empty-state/no-invite.svg";
import NoManageAccess from "/images/empty-state/no_access_managment.svg";

// ── Remove / destructive confirmations ───────────────────────────────────────
import RemoveWorkspace from "/images/remove/remove-workspace.svg";
import RemoveProject from "/images/remove/remove-project.svg";
import RemoveMembers from "/images/remove/remove-members.svg";

// ── Portal cards ─────────────────────────────────────────────────────────────
import ActiveCard from "/images/portal-cards/active-card.svg";
import ArchivedCard from "/images/portal-cards/archived-card.svg";
import TotalCard from "/images/portal-cards/total-card.svg";

// ── File-format icons ─────────────────────────────────────────────────────────
import DocIcon from "/images/file-format/docx.svg";
import PdfIcon from "/images/file-format/pdf.svg";
import TextIcon from "/images/file-format/text.svg";
import FolderIcon from "/images/file-format/folder.svg";
import SheetIcon from "/images/file-format/sheet.svg";
import ImageIcon from "/images/file-format/image.svg";
import VideoIcon from "/images/file-format/video.svg";
import IfcIcon from "/images/file-format/ifc.svg";
import PptxIcon from "/images/file-format/pptx.svg";
import DWGIcon from "/images/file-format/dwg.svg";

// ── Misc icons (public/images/icons/) ────────────────────────────────────────
import CheckIcon from "/images/icons/check.svg";
import MoreHorizontalSquare from "/images/icons/more-horizontal-square-01.svg";

// ── Loading animations ────────────────────────────────────────────────────────
import LoadingShape from "/images/loading/shape.svg";
import LoadingPoints from "/images/loading/points.svg";

// ── Flags ─────────────────────────────────────────────────────────────────────
import DzFlag from "/images/flags/dz-flag.svg";

// ─────────────────────────────────────────────────────────────────────────────

export type ImageKey =
  // Logo
  | "logo"
  // Page-header
  | "workspace-icon"
  | "docs-icon"
  | "approval-icon"
  | "members-header"
  | "projects-header"
  | "logs-header"
  | "setting-header"
  | "portal-header"
  | "trash-icon"
  | "manager-access-icon"
  // Empty states
  | "empty-admins"
  | "no-docs"
  | "empty-workspace"
  | "no-workspace-projects"
  | "no-workspace-projects-table"
  | "no-members"
  | "no-active-approvals"
  | "no-recent-activity"
  | "no-search-docs"
  | "no-recent-documents"
  | "no-license"
  | "no-invites"
  | "no-manage-access"
  // Remove / destructive
  | "remove-workspace"
  | "remove-project"
  | "remove-members"
  // Portal cards
  | "active-card"
  | "archived-card"
  | "total-card"
  // File-format icons
  | "doc-icon"
  | "pdf-icon"
  | "text"
  | "folder-icon"
  | "sheet-icon"
  | "image"
  | "video"
  | "ifc"
  | "pptx"
  | "dwg"
  // Misc icons
  | "check-icon"
  | "more-horizontal-square"
  // Loading
  | "loading-shape"
  | "loading-points"
  // Flags
  | "dz-flag"
  | "license-header";

export const images: Record<ImageKey, string> = {
  // Logo
  logo,
  // Page-header
  "workspace-icon": WorkspaceIcon,
  "docs-icon": DocsIcon,
  "approval-icon": ApprovalIcon,
  "members-header": MembersIcon,
  "projects-header": ProjectsIcon,
  "logs-header": LogsIcon,
  "setting-header": SettingsIcon,
  "trash-icon": TrashIcon,
  "portal-header": PortalIcon,
  "manager-access-icon": ManageAccessIcon,
  // Empty states
  "empty-admins": NoAdmins,
  "no-docs": NoDocs,
  "empty-workspace": EmptyWorkspace,
  "no-workspace-projects": NoWorkspaceProjects,
  "no-workspace-projects-table": NoWorkspaceProjectsTable,
  "no-members": NoMembers,
  "no-active-approvals": NoActiveApprovals,
  "no-recent-activity": NoRecentActivity,
  "no-search-docs": NoSearchDocs,
  "no-recent-documents": NoRecentDocuments,
  "no-license": NoLicense,
  "no-invites": NoInivtes,
  "no-manage-access": NoManageAccess,
  // Remove / destructive
  "remove-workspace": RemoveWorkspace,
  "remove-project": RemoveProject,
  "remove-members": RemoveMembers,
  // Portal cards
  "active-card": ActiveCard,
  "archived-card": ArchivedCard,
  "total-card": TotalCard,
  // File-format icons
  "doc-icon": DocIcon,
  "pdf-icon": PdfIcon,
  text: TextIcon,
  "folder-icon": FolderIcon,
  "sheet-icon": SheetIcon,
  image: ImageIcon,
  video: VideoIcon,
  ifc: IfcIcon,
  pptx: PptxIcon,
  dwg: DWGIcon,
  // Misc icons
  "check-icon": CheckIcon,
  "more-horizontal-square": MoreHorizontalSquare,
  // Loading
  "loading-shape": LoadingShape,
  "loading-points": LoadingPoints,
  // Flags
  "dz-flag": DzFlag,
  "license-header": LicenseIcon,
};
